import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Menu,
  ListItemIcon
} from "@mui/material";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import CardHeadMenu from "../../Components/Card/CardHeadMenu/CardHeadMenu";
import { rSetShowFab } from "../../redux/reducers/GlobalVariables";
import configServ from "../../services/config";
import CameraCard from "./CameraCard";
import EmployeeTable from "./EmployeeTable";
import MultiPageDialog from "./MultiPageDialog";
import { use } from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import DownloadEmployeePdf from "../../Components/Download/DownloadEmployeePdf";
import DownloadEmployeeExcel from "../../Components/Download/DownloadEmployeeExcel";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import BulkUploadDialog from "./BulkUploadDialog";
import axios from "axios";
export const appServiceName = process.env.REACT_APP_BACKEND_URL;

const getEmptyForm = (companyCountry = "India") => ({
  name: "",
  email: "",
  mobile: "",
  address: "",
  country: companyCountry || "India",
  State: "",
  city: "",
  Pin: "",
  employee_code: "",
  designation_id: "",
  department_id: "",
  status: true,
  site_ids: [],
});

const isActiveStatus = (value) => value === true || value === 1 || value === "1";

const optionalValue = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  return value;
};

const getErrorMessage = (error, fallback = "Something went wrong") => {
  return (
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message ||
    fallback
  );
};


const getEmployeeDisplayName = (employee) => {
  return (
    employee?.contact_name ||
    `${employee?.fname || ""} ${employee?.lname || ""}`.trim() ||
    "-"
  );
};

const formatDetailValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

function EmployeeDetailsDialog({ open, employee, onClose }) {
  const details = [
    { label: "Name", value: getEmployeeDisplayName(employee) },
    { label: "Employee Code", value: employee?.employee_code },
    { label: "Phone", value: employee?.phone },
    { label: "Mobile", value: employee?.mobile },
    { label: "Email", value: employee?.email },
    { label: "Address", value: employee?.address },
    { label: "City", value: employee?.city },
    { label: "State", value: employee?.State },
    { label: "Country", value: employee?.country },
    { label: "Pin", value: employee?.Pin },
    { label: "Designation", value: employee?.designation_name },
    { label: "Department", value: employee?.department_name },
    {
      label: "Site",
      value: employee?.site_names?.join(", ") || "-"
    },

    {
      label: "Status",
      value:
        employee?.status === null || employee?.status === undefined
          ? "-"
          : isActiveStatus(employee.status)
            ? "ACTIVE"
            : "INACTIVE",
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Employee Details</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {details.map((item) => (
            <Grid item xs={12} sm={6} key={item.label}>
              <Typography variant="caption" color="text.secondary">
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
                {formatDetailValue(item.value)}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EmployeeCreateWithUserForm({
  isEdit,
  editData,
  designationList,
  departmentList,
  onCancel,
  onSuccess,
}) {
  const { company_id, user_id, companyCountry } = useSelector(
    (state) => state.GlobalVariables
  );
  const [form, setForm] = useState(() => getEmptyForm(companyCountry));
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const fieldSx = { backgroundColor: "white" };
  const [siteList, setSiteList] = useState([]);

  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const openExportMenu = Boolean(exportAnchorEl);
  const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportClose = () => setExportAnchorEl(null);

  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);

  const buildFormFromEditData = useCallback(() => {
    if (!isEdit || !editData?.id) return getEmptyForm(companyCountry);

    return {
      ...getEmptyForm(companyCountry),
      name:
        editData.contact_name ||
        `${editData.fname || ""} ${editData.lname || ""}`.trim(),
      email: editData.email || "",
      mobile: editData.mobile || "",
      address: editData.address || "",
      country: editData.country || companyCountry || "India",
      State: editData.State || "",
      city: editData.city || "",
      Pin: editData.Pin || "",
      employee_code: editData.employee_code || "",
      designation_id: editData.designation_id || "",
      department_id: editData.department_id || "",
      site_ids: editData.site_ids || [], // ✅ Add this
      status: isActiveStatus(editData.status),
    };
  }, [companyCountry, editData, isEdit]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await configServ.getCountry();
        setCountryList(res?.status === 200 ? res.data || [] : []);
      } catch {
        setCountryList([]);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    if (!form.country || countryList.length === 0) {
      setStateList([]);
      return;
    }

    const fetchStates = async () => {
      try {
        const selectedCountry = countryList.find(
          (item) => item.country === form.country
        );

        if (!selectedCountry?.PK) {
          setStateList([]);
          return;
        }

        const res = await configServ.getState({ country: selectedCountry.PK });
        setStateList(res?.status === 200 ? res.data || [] : []);
      } catch {
        setStateList([]);
      }
    };

    fetchStates();
  }, [countryList, form.country]);

  useEffect(() => {
    setForm(buildFormFromEditData());
    setErrors({});
  }, [buildFormFromEditData]);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "country" ? { State: "" } : {}),
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    const email = form.email.trim();

    if (!form.name.trim()) nextErrors.name = "Name is required";
    if (!form.mobile.trim()) nextErrors.mobile = "Mobile number is required";
    if (!isEdit && !form.country.trim()) nextErrors.country = "Country is required";
    if (!isEdit && !form.State.trim()) nextErrors.State = "State is required";
    if (!isEdit && !form.city.trim()) nextErrors.city = "City is required";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors.email = "Enter a valid email";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleReset = () => {
    setForm(buildFormFromEditData());
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const employeeFields = {
        employee_code: optionalValue(form.employee_code.trim()),
        designation_id: optionalValue(form.designation_id),
        department_id: optionalValue(form.department_id),
        site_ids: form.site_ids || [],
      };

      const res = isEdit
        ? await configServ.updateEmployeeWithUser(editData.id, {
          company_id,
          name: form.name.trim(),
          mobile: form.mobile.trim(),
          email: optionalValue(form.email.trim()),
          address: optionalValue(form.address.trim()),
          country: optionalValue(form.country.trim()),
          State: optionalValue(form.State.trim()),
          city: optionalValue(form.city.trim()),
          Pin: optionalValue(form.Pin.trim()),
          ...employeeFields,
          status: Boolean(form.status),
          updated_by: user_id,
        })
        : await configServ.createEmployeeWithUser({
          company_id,
          name: form.name.trim(),
          email: optionalValue(form.email.trim()),
          mobile: form.mobile.trim(),
          address: optionalValue(form.address.trim()),
          country: form.country.trim(),
          State: form.State.trim(),
          city: form.city.trim(),
          Pin: optionalValue(form.Pin.trim()),
          ...employeeFields,
          created_by: user_id,
        });

      if (res?.status === true) {
        toast.success(res?.message || (isEdit ? "Employee updated" : "Employee created"));
        setForm(getEmptyForm(companyCountry));
        setErrors({});
        onSuccess();
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };


  const fetchSiteList = async () => {
    try {
      const result = await configServ.get_customer({ company_id });
      const filteredSites = result?.filter((item) => item.type === "Standard");
      setSiteList(filteredSites || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchSiteList();
  }, []);

  const handleBulkUpload = async (file) => {
    try {
      setBulkUploadLoading(true);

      const formData = new FormData();

      formData.append("file", file);
      formData.append("company_id", company_id);

      const response = await axios.post(
        `${appServiceName}/api/employees/bulk-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const data = response.data;

      /*
      |--------------------------------------------------------------------------
      | Download Failed Records
      |--------------------------------------------------------------------------
      */

      if (data?.failed && data.failed.length > 0) {
        let txtContent = "";

        txtContent += "EMPLOYEE BULK UPLOAD FAILED RECORDS\n";
        txtContent += "====================================\n\n";

        txtContent += `Total Records: ${data.total || 0}\n`;
        txtContent += `Success: ${data.success_count || 0}\n`;
        txtContent += `Failed: ${data.failed_count || 0}\n\n`;

        txtContent += "FAILED RECORDS\n";
        txtContent += "--------------\n\n";

        data.failed.forEach((item, index) => {
          txtContent += `Record ${index + 1}\n`;
          txtContent += `Row: ${item.row || "-"}\n`;
          txtContent += `Name: ${item.name || "-"}\n`;
          txtContent += `Error: ${item.error || "Unknown error"}\n`;
          txtContent += "------------------------------------\n";
        });

        const blob = new Blob(
          [txtContent],
          {
            type: "text/plain;charset=utf-8",
          }
        );

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;
        link.download = `employees_bulk_upload_failed_${new Date()
          .toISOString()
          .slice(0, 10)}.txt`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
      }

      /*
      |--------------------------------------------------------------------------
      | Success Response
      |--------------------------------------------------------------------------
      */

      if (data?.status === true) {
        toast.success(
          data?.message || "File uploaded successfully."
        );

        onSuccess();
        setBulkUploadOpen(false);

        return;
      }

      /*
      |--------------------------------------------------------------------------
      | Failed Response
      |--------------------------------------------------------------------------
      */

      toast.error(
        data?.message || "Failed to upload file."
      );

    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        "Failed to upload file."
      );
    } finally {
      setBulkUploadLoading(false);
    }
  };

  return (
    <>
      <BulkUploadDialog open={bulkUploadOpen} onClose={() => setBulkUploadOpen(false)} loading={bulkUploadLoading} onUpload={handleBulkUpload} />

      <Card sx={{ m: 2, borderRadius: 2, backgroundColor: "#e3f3fc" }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
            {isEdit ? "Edit Employee" : "Create Employee"}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Name *"
                name="name"
                value={form.name}
                onChange={handleChange}
                error={Boolean(errors.name)}
                helperText={errors.name}
                fullWidth
                size="small"
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Mobile *"
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                error={Boolean(errors.mobile)}
                helperText={errors.mobile}
                fullWidth
                size="small"
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                error={Boolean(errors.email)}
                helperText={errors.email}
                fullWidth
                size="small"
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Address"
                name="address"
                value={form.address}
                onChange={handleChange}
                fullWidth
                size="small"
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" error={Boolean(errors.country)}>
                <InputLabel>Country *</InputLabel>
                <Select
                  name="country"
                  value={form.country}
                  label="Country *"
                  onChange={handleChange}
                  sx={fieldSx}
                >
                  <MenuItem value="">Select Country</MenuItem>
                  {form.country && !countryList.some((item) => item.country === form.country) && (
                    <MenuItem value={form.country}>{form.country}</MenuItem>
                  )}
                  {countryList.map((item) => (
                    <MenuItem key={item.PK || item.country} value={item.country}>
                      {item.country}
                    </MenuItem>
                  ))}
                </Select>
                {errors.country && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.country}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" error={Boolean(errors.State)}>
                <InputLabel>State *</InputLabel>
                <Select
                  name="State"
                  value={form.State}
                  label="State *"
                  onChange={handleChange}
                  sx={fieldSx}
                  disabled={!form.country}
                >
                  <MenuItem value="">Select State</MenuItem>
                  {form.State && !stateList.some((item) => item.State === form.State) && (
                    <MenuItem value={form.State}>{form.State}</MenuItem>
                  )}
                  {stateList.map((item) => (
                    <MenuItem key={item.PK || item.State} value={item.State}>
                      {item.State}
                    </MenuItem>
                  ))}
                </Select>
                {errors.State && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.State}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="City *"
                name="city"
                value={form.city}
                onChange={handleChange}
                error={Boolean(errors.city)}
                helperText={errors.city}
                fullWidth
                size="small"
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Pin"
                name="Pin"
                value={form.Pin}
                onChange={handleChange}
                fullWidth
                size="small"
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Employee Code"
                name="employee_code"
                value={form.employee_code}
                onChange={handleChange}
                helperText={!isEdit ? "Leave blank to auto-generate" : ""}
                fullWidth
                size="small"
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Designation</InputLabel>
                <Select
                  name="designation_id"
                  value={form.designation_id}
                  label="Designation"
                  onChange={handleChange}
                  sx={fieldSx}
                >
                  <MenuItem value="">None</MenuItem>
                  {designationList.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  name="department_id"
                  value={form.department_id}
                  label="Department"
                  onChange={handleChange}
                  sx={fieldSx}
                >
                  <MenuItem value="">None</MenuItem>
                  {departmentList.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Sites</InputLabel>

                <Select
                  multiple
                  name="site_ids"
                  value={form.site_ids}
                  label="Sites"
                  onChange={handleChange}
                  renderValue={(selected) =>
                    siteList
                      .filter((site) => selected.includes(site.id))
                      .map((site) => site.fname)
                      .join(", ")
                  }
                  sx={fieldSx}
                >
                  {siteList.map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      <Checkbox checked={form.site_ids.includes(site.id)} />
                      <ListItemText primary={site.fname} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {isEdit && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="status"
                      checked={Boolean(form.status)}
                      onChange={handleChange}
                    />
                  }
                  label="Active"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Stack direction="row" justifyContent="flex-end" spacing={1}>

                <Button
                  variant="contained"
                  color="info"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => setBulkUploadOpen(true)}
                  disabled={loading}
                >
                  Bulk Upload
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<RotateLeftIcon />}
                  onClick={handleReset}
                  disabled={loading}
                >
                  Reset
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  onClick={onCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  startIcon={
                    loading ? <CircularProgress size={16} /> : <SaveIcon />
                  }
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {isEdit ? "Update" : "Save"}
                </Button>

              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
}

const NewEmployeePage = () => {
  const { company_id, user_id, is_super, is_admin, permissions, companyName } = useSelector(
    (state) => state.GlobalVariables
  );
  const dispatch = useDispatch();

  const [isCreate, setIsCreate] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState({});
  const [employeeList, setEmployeeList] = useState([]);
  const [loader, setLoader] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [designationList, setDesignationList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [reloadKey, setReloadKey] = useState(0);
  const [openCamera, setOpenCamera] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [employeeDetailsOpen, setEmployeeDetailsOpen] = useState(false);
  const [selectedEmployeeDetails, setSelectedEmployeeDetails] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");

  const fetchEmployees = useCallback(async () => {
    if (!company_id || !user_id) return;

    setLoader(true);
    try {
      const payload = {
        company_id,
        user_id,
        is_admin: Boolean(is_super || is_admin),
        page: page + 1,
        per_page: rowsPerPage,
      };

      if (appliedSearch) payload.search = appliedSearch;
      if (statusFilter !== "all") payload.status = statusFilter;
      if (departmentFilter) payload.department_id = departmentFilter;
      if (siteFilter) payload.site_id = siteFilter;

      const res = await configServ.getEmployees(payload);
      if (res?.status === true) {
        setEmployeeList(res.data || []);
        setTotal(res.pagination?.total || 0);
      } else {
        setEmployeeList([]);
        setTotal(0);
        toast.error(res?.message || "Failed to fetch employees");
      }
    } catch (error) {
      setEmployeeList([]);
      setTotal(0);
      toast.error(getErrorMessage(error, "Failed to fetch employees"));
    } finally {
      setLoader(false);
    }
  }, [
    appliedSearch,
    company_id,
    is_admin,
    is_super,
    page,
    rowsPerPage,
    statusFilter,
    departmentFilter, // Dependency added
    siteFilter,       // Dependency added
    user_id,
  ]);

  const fetchLookupData = useCallback(async () => {
    if (!company_id) return;

    try {
      const [designationRes, departmentRes] = await Promise.all([
        configServ.getAllDesignations(company_id),
        configServ.getAllDepartments(company_id),
      ]);

      setDesignationList(designationRes?.status === true ? designationRes.data || [] : []);
      setDepartmentList(departmentRes?.status === true ? departmentRes.data || [] : []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load employee lookup data"));
    }
  }, [company_id]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees, reloadKey]);

  useEffect(() => {
    if (isCreate) fetchLookupData();
  }, [fetchLookupData, isCreate]);

  const closeForm = () => {
    setIsCreate(false);
    setIsEdit(false);
    setEditData({});
    dispatch(rSetShowFab(true));
  };

  const handlePopup = () => {
    if (isCreate) {
      closeForm();
      return;
    }

    setIsCreate(true);
    setIsEdit(false);
    setEditData({});
    dispatch(rSetShowFab(false));
  };

  const handleSuccess = () => {
    closeForm();
    setReloadKey((prev) => prev + 1);
  };

  const handleEdit = (row) => {
    setEditData(row);
    setIsEdit(true);
    setIsCreate(true);
    dispatch(rSetShowFab(false));
    window.scrollTo({ top: 0 });
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Deactivate Employee?",
      text: "The employee will be marked inactive.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, deactivate",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await configServ.deleteEmployee(id);
      if (res?.status === true) {
        toast.success(res?.message || "Employee deactivated");
        setReloadKey((prev) => prev + 1);
      } else {
        toast.error(res?.message || "Failed to deactivate employee");
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to deactivate employee"));
    }
  };

  const handleOpenCamera = (employee) => {
    setSelectedId(employee?.id || employee?.subordinate_id);
    setSelectedUserId(employee?.customer_id || employee?.subordinate_customer_id);
    setOpenCamera(true);
  };

  const handleOpenMoreDetails = (employee) => {
    // const employee =
    //   typeof employeeOrId === "object"
    //     ? employeeOrId
    //     : employeeList.find((item) => item.id === employeeOrId);

    setSelectedId(employee?.id || employee?.subordinate_id);
    setSelectedUserId(employee?.customer_id || employee?.subordinate_customer_id);
    setDialogOpen(true);
  };

  const handleOpenEmployeeDetails = (employee) => {
    setSelectedEmployeeDetails(employee);
    setEmployeeDetailsOpen(true);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(0);
    setAppliedSearch(search.trim());
  };


  // Fetch Departments
  const fetchDepartments = async () => {
    try {
      if (company_id) {
        const deptRes = await configServ.getAllDepartments(company_id);
        if (deptRes?.data) {
          setDepartments(deptRes.data);
        } else if (Array.isArray(deptRes)) {
          setDepartments(deptRes);
        }
      }
    } catch (e) {
      console.error("Error fetching departments:", e);
    }
  };

  // Fetch Sites (Filtered by type "Standard")
  const fetchSiteList = async () => {
    try {
      const result = await configServ.get_customer({ company_id });
      const filteredSites = result?.filter((item) => item.type === "Standard");
      setSiteList(filteredSites || []);
    } catch (e) {
      console.error("Error fetching site list:", e);
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchSiteList();
    assignCompanyLogo();
  }, []);


  const assignCompanyLogo = async () => {
    try {
      const result = await configServ.company_logo({ company_id: company_id });
      if (result && result.image) {
        // Assuming the result.image is already a base64 string
        setCompanyLogo(`data:image/png;base64,${result.image}`);
      } else {
        setCompanyLogo(null); // Set as null if no logo
      }
    } catch (e) {
      console.log(e);
      setCompanyLogo(null); // Handle error by clearing logo
    }
  };



  // Export Menu Anchor State
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const openExportMenu = Boolean(exportAnchorEl);

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  // Dummy / Handler functions for PDF & Excel download
  const handleDownloadPdf = () => {
    DownloadEmployeePdf(
      employeeList,
      companyName || "Your Company",
      companyLogo,
      setLoader // or setOpenBackdrop
    );
  };

  const handleDownloadExcel = () => {
    DownloadEmployeeExcel(
      employeeList,
      companyName || "Your Company",
    );
  };
  return (
    <Box sx={{ width: "100%", mt: 2, mb: 4 }}>
      <Dialog
        open={openCamera}
        onClose={() => setOpenCamera(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent sx={{ p: 0 }}>
          <CameraCard
            id={selectedId}
            user_id={selectedUserId}
            onClose={() => setOpenCamera(false)}
          />
        </DialogContent>
      </Dialog>

      <CardHeadMenu
        prefix="Na"
        suffix="Na"
        title="EMPLOYEES"
        handlePopup={handlePopup}
        isEdit={isEdit}
        create={isCreate}
        showCreate={Boolean(is_super || is_admin || permissions?.includes(83))}
      />

      <MultiPageDialog
        open={dialogOpen}
        handleClose={() => setDialogOpen(false)}
        id={selectedId}
        user_id={selectedUserId}
      />

      <EmployeeDetailsDialog
        open={employeeDetailsOpen}
        employee={selectedEmployeeDetails}
        onClose={() => setEmployeeDetailsOpen(false)}
      />

      {isCreate && (
        <EmployeeCreateWithUserForm
          isEdit={isEdit}
          editData={editData}
          designationList={designationList}
          departmentList={departmentList}
          onCancel={closeForm}
          onSuccess={handleSuccess}
        />
      )}

      <Card sx={{ m: 0, borderRadius: 2 }}>
        <CardContent>
          <Box component="form" onSubmit={handleSearchSubmit}>
            <Grid container spacing={1.5} alignItems="center">
              {/* Title & Total */}
              <Grid item xs={12} sm={6} md={1.5}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Employee List
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                >
                  Total: {total}
                </Typography>
              </Grid>

              {/* Search Input */}
              <Grid item xs={12} sm={6} md={2.5}>
                <TextField
                  label="Search employees"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Name, mobile, code, designation"
                  fullWidth
                  size="small"
                />
              </Grid>

              {/* Status Dropdown */}
              <Grid item xs={12} sm={6} md={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(event) => {
                      setStatusFilter(event.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Site Dropdown */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="site-select-label">Site</InputLabel>
                  <Select
                    labelId="site-select-label"
                    value={siteFilter}
                    label="Site"
                    onChange={(event) => {
                      setSiteFilter(event.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">
                      <em>All Sites</em>
                    </MenuItem>
                    {siteList.map((site) => (
                      <MenuItem
                        key={site.id || site.site_id}
                        value={site.id || site.site_id}
                      >
                        {site.contact_name ||
                          site.site_name ||
                          site.name ||
                          `Site ${site.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Department Dropdown */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="department-select-label">Department</InputLabel>
                  <Select
                    labelId="department-select-label"
                    value={departmentFilter}
                    label="Department"
                    onChange={(event) => {
                      setDepartmentFilter(event.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">
                      <em>All Departments</em>
                    </MenuItem>
                    {departments.map((dept) => (
                      <MenuItem
                        key={dept.id || dept.department_id}
                        value={dept.id || dept.department_id}
                      >
                        {dept.department_name ||
                          dept.name ||
                          `Department ${dept.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Search Button */}
              <Grid item xs={12} sm={6} md={1.2}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SearchIcon />}
                  fullWidth
                  sx={{ height: "40px" }}
                >
                  Search
                </Button>
              </Grid>

              {/* Export / Download Dropdown */}
              <Grid item xs={12} sm={6} md={1.3}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<FileDownloadIcon />}
                  fullWidth
                  onClick={handleExportClick}
                  sx={{ height: "40px", whiteSpace: "nowrap" }}
                >
                  Export
                </Button>
                <Menu
                  anchorEl={exportAnchorEl}
                  open={openExportMenu}
                  onClose={handleExportClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handleExportClose();
                      handleDownloadPdf && handleDownloadPdf();
                    }}
                  >
                    <ListItemIcon>
                      <PictureAsPdfIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>Download PDF</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleExportClose();
                      handleDownloadExcel && handleDownloadExcel();
                    }}
                  >
                    <ListItemIcon>
                      <TableViewIcon fontSize="small" color="success" />
                    </ListItemIcon>
                    <ListItemText>Download Excel</ListItemText>
                  </MenuItem>
                </Menu>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      <EmployeeTable
        employeeList={employeeList}
        loader={loader}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        handleOpenCamera={handleOpenCamera}
        handleOpenMoreDetails={handleOpenMoreDetails}
        handleOpenEmployeeDetails={handleOpenEmployeeDetails}
        showTitle={false}
      />
    </Box>
  );
};

export default NewEmployeePage;
